import { CloudDownload } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import Papa from 'papaparse';
import React, { useContext, useState } from 'react';
import { AuthContext } from '../../common/Auth';
import { newTheme } from '../../components/theme/theme';
import { useThemeSettings } from '../../layouts/state/useThemeSettings';
import { AetherInventoryReportRecord } from '../../models/ManagementProduct';
import { Warehouse } from '../../models/Warehouse';
import { useGetInventoryReportByWarehouseQuery } from '../../redux/api/productApi';
import { useGetUserWarehousesQuery } from '../../redux/api/userApi';
import MainBreadCrumb from '../../wrappers/breadcrumb/MainBreadCrumb';
import AetherInventoryReportTable from './AetherInventoryReportTable';

const csvColumns: { id: keyof AetherInventoryReportRecord; label: string }[] = [
  {
    id: 'productName',
    label: 'Product Name',
  },
  {
    id: 'itemNo',
    label: 'Item No',
  },
  {
    id: 'inHouseId',
    label: 'In House ID',
  },
  {
    id: 'color',
    label: 'Color',
  },
  {
    id: 'size',
    label: 'Size',
  },
  {
    id: 'sku',
    label: 'Sku',
  },
  {
    id: 'quantity',
    label: 'Quantity',
  },
  {
    id: 'minimum',
    label: 'Minimum',
  },
];

export default function InventoryReportPage() {
  const { settingData } = useContext(AuthContext);
  const { themedefaultColor, themedefaultTextColor, defaultFont } = useThemeSettings({
    settingData,
  });
  const { data: warehouses, isLoading: loadingWarehouses } = useGetUserWarehousesQuery();
  const [warehouseId, setWarehouseId] = useState<string | null>(null);

  const { data: records, isLoading: loadingRecords } = useGetInventoryReportByWarehouseQuery(
    warehouseId ?? skipToken,
  );

  const handleChangeWarehouseId = (_: React.SyntheticEvent, warehouse: Warehouse | null) => {
    setWarehouseId(warehouse ? warehouse._id : null);
  };

  const jsonToCsv = () => {
    if (!records) {
      return '';
    }
    const formattedRows: Record<string, string | number>[] = [];
    for (const row of records) {
      const formattedRow: Record<string, string | number> = {};
      for (const column of csvColumns) {
        formattedRow[column.label] = row[column.id];
      }
      formattedRows.push(formattedRow);
    }
    const csv = Papa.unparse(formattedRows);
    return csv;
  };

  return (
    <ThemeProvider
      theme={newTheme({ themedefaultColor, themedefaultTextColor, fontFamily: defaultFont })}
    >
      <MainBreadCrumb />
      <Container maxWidth="xl">
        <Stack spacing={2}>
          <Typography variant="h4" gutterBottom>
            Third Party Inventory Report
          </Typography>
          <Card>
            <CardContent>
              <Stack direction={'row'} spacing={2}>
                <Autocomplete
                  sx={{ flex: 3 }}
                  options={warehouses || []}
                  getOptionLabel={(option) => option.name}
                  getOptionKey={(option) => option._id}
                  loading={loadingWarehouses}
                  onChange={handleChangeWarehouseId}
                  renderInput={(params) => <TextField {...params} label="Warehouse" />}
                />
                <Button
                  sx={{ flex: 1 }}
                  startIcon={<CloudDownload />}
                  disabled={!warehouseId || loadingRecords}
                  color="primary"
                  fullWidth
                  variant="outlined"
                  type="submit"
                  download={'inventory-report.csv'}
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(jsonToCsv())}`}
                >
                  Download Report
                </Button>
              </Stack>
            </CardContent>
          </Card>
          <AetherInventoryReportTable records={records} loadingRecords={loadingRecords} />
        </Stack>
      </Container>
    </ThemeProvider>
  );
}
